import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  addUnusualPeriod,
  addUnusualPeriodFailed,
  addUnusualPeriodSuccessfuly,
} from '@lessor/core/store/unusual-period/unusual-period.actions';
import { UtilService } from '@lessor/services/util/util.service';
import { BookingType, Court, Sport, UnusualPeriodRequest } from '@libs/entity-lib';
import { InputMaskLibModule } from '@libs/input-mask';
import { QuickCreatePayload } from '@libs/util-services';
import { take } from 'rxjs/operators';

/**
 * Modal compacto pra criar `unusual_period` (horário especial) a partir do
 * click em célula vazia da grade. Pré-preenche tudo que dá pelo `payload`
 * (data, hora, quadra, duração, preço sugerido) — usuário só confirma o
 * esporte e (opcionalmente) ajusta preço/motivo.
 *
 * Diferente do `editor-unusual-period`, este modal NÃO suporta edição,
 * multi-quadra ou janelas longas. É um quick-create de 1 slot único.
 */
import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonSelect, IonSelectOption, IonSpinner, IonTitle, IonToolbar } from '@ionic/angular/standalone';

@Component({
  selector: 'lessor-quick-create-modal',
  templateUrl: './quick-create-modal.component.html',
  styleUrls: ['./quick-create-modal.component.scss'],
  imports: [
    IonButton,
    IonButtons,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonSelect,
    IonSelectOption,
    IonSpinner,
    IonTitle,
    IonToolbar,
    CommonModule,
    ReactiveFormsModule,
    InputMaskLibModule
  ],
  // UtilService NÃO é providedIn:'root' — cada feature provê localmente
  // (padrão já visto em home.page.ts, court-card, rent-type-form).
  providers: [UtilService],
})
export class QuickCreateModalComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly modalCtrl = inject(ModalController);
  private readonly destroyRef = inject(DestroyRef);
  private readonly utilService = inject(UtilService);

  // Atribuído via ModalController.componentProps (Object.assign).
  @Input() payload!: QuickCreatePayload;

  /** True enquanto a dispatch está em voo. Desabilita botão "Criar". */
  isSubmitting = signal(false);

  /**
   * Esportes da quadra. Carregados via UtilService DIRETO (não passa pelo
   * store) — pois `util.effects.ts:loadSportsCourt$` tem bug pré-existente
   * (ofType errado, escuta fetchStatusCourt em vez de fetchSportsCourt).
   * Pra evitar dependência do bug, busco via Observable e popolo o signal.
   */
  sports = signal<Sport[]>([]);

  form = new FormGroup({
    sportId: new FormControl<number | null>(null, Validators.required),
    price: new FormControl<string>('', Validators.required),
    reason: new FormControl<string>('Horário estendido', Validators.required),
    maxPlayers: new FormControl<number>(1, [
      Validators.required,
      Validators.min(1),
    ]),
    slotType: new FormControl<BookingType>(BookingType.ALUGUEL),
    // Duração em minutos — editável. Default = sugerido pelo payload.
    // Permite ao user escolher slot mais longo (ex: 60min em vez de 20).
    durationMin: new FormControl<number>(20, [
      Validators.required,
      Validators.min(5),
      Validators.max(240),
    ]),
  });

  /** Reflexão signal da duração atual do form pra `endTimeLabel` reagir. */
  private readonly durationSignal = signal<number>(20);

  /**
   * Hora final calculada = slotTime + duração editável. Reativa: muda quando
   * usuário ajusta a duração no form. Padrão pra +1 ano de UX coerente —
   * sempre que o user escolher duração, ele vê a hora final mudar live.
   */
  readonly endTimeLabel = computed(() => {
    if (!this.payload) return '';
    const start = parseHHmm(this.payload.slotTime);
    if (start == null) return '';
    const dur = this.durationSignal();
    const endMin = start + dur;
    const h = Math.floor((endMin % (24 * 60)) / 60);
    const m = endMin % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });

  /**
   * Opções de duração no select rápido. Cobre os casos comuns dos centros
   * que vimos (20/30/45/60/90/120). User pode digitar custom também.
   */
  readonly durationOptions = [20, 30, 45, 60, 90, 120];

  /**
   * Detecta conflito de horário com slots existentes do dia. Pre-validação
   * client-side antes de submeter — backend também valida, mas feedback
   * imediato é melhor UX. Regra padrão de overlap: [aStart, aEnd) sobrepõe
   * [bStart, bEnd) se aStart < bEnd && aEnd > bStart. Intervalos
   * adjacentes (ex: 11:00-12:00 e 12:00-13:00) NÃO conflitam.
   *
   * Reage via `durationSignal` — recalcula quando user muda duração.
   */
  readonly conflictEvent = computed<{ start: string; end: string } | null>(() => {
    if (!this.payload) return null;
    const existing = this.payload.existingEvents ?? [];
    if (existing.length === 0) return null;
    const newStart = parseHHmm(this.payload.slotTime);
    if (newStart == null) return null;
    const newEnd = newStart + this.durationSignal();
    for (const ev of existing) {
      const s = parseHHmm(ev.start);
      const e = parseHHmm(ev.end);
      if (s == null || e == null) continue;
      if (newStart < e && newEnd > s) return ev;
    }
    return null;
  });

  readonly hasConflict = computed(() => this.conflictEvent() !== null);

  /** Label "Quarta, 27 de mai" formatado pra header. */
  readonly dateLabel = computed(() => {
    if (!this.payload?.date) return '';
    try {
      const parsed = parse(this.payload.date, 'yyyy-MM-dd', new Date());
      const formatted = format(parsed, "EEE, d 'de' MMM", { locale: ptBR });
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
      return this.payload.date;
    }
  });

  ngOnInit(): void {
    // O pré-preenchimento do PREÇO é feito pela diretiva `maskBrlCurrencyFormat`
    // (lê `[maskBrlCurrencyFormat]="payload?.suggestedPrice"` e popula o form).

    // Inicializa duração com a sugestão do payload (GCD dos slots do dia).
    const initialDuration = this.payload?.durationMin ?? 20;
    this.form.controls.durationMin.setValue(initialDuration);
    this.durationSignal.set(initialDuration);

    // Sincroniza signal de duração com mudanças do form (template usa o signal
    // pra computed `endTimeLabel` reagir live ao input do user).
    this.form.controls.durationMin.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        if (typeof v === 'number' && v >= 5) this.durationSignal.set(v);
      });

    // Carrega esportes do centro DIRETAMENTE via UtilService (Observable).
    // BYPASS do store/effect porque `util.effects.ts:loadSportsCourt$` tem
    // bug pré-existente (ofType escuta fetchStatusCourt). Aqui garantimos
    // que o dropdown popula sempre que o modal abre.
    //
    // FILTRO POR QUADRA: payload.courtSportIds vem da `agenda.component`
    // (lookup no store de courts) — restringe a lista aos esportes da
    // quadra clicada. Sem isso, listaríamos tênis em quadra de Wake — ruim.
    // Vazio/undefined: sem filtro (fallback).
    this.utilService
      .fetchSportsCourt()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((list) => {
        const all = list ?? [];
        const allowed = this.payload?.courtSportIds;
        const filtered =
          allowed && allowed.length > 0
            ? all.filter((s) => s.id != null && allowed.includes(s.id))
            : all;
        this.sports.set(filtered);
        this.tryPreselectSport();
      });

    // Listeners de sucesso/erro do dispatch. Dismiss com `created: true` em
    // sucesso pra a agenda recarregar via output `emptySlotClick → handler`.
    this.actions$
      .pipe(
        ofType(addUnusualPeriodSuccessfuly),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isSubmitting.set(false);
        this.modalCtrl.dismiss({ created: true });
      });

    this.actions$
      .pipe(
        ofType(addUnusualPeriodFailed),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isSubmitting.set(false);
      });
  }

  /**
   * Pré-seleciona o único esporte da lista quando há um só. Idempotente —
   * se já houver valor no controle, não sobrescreve (preserva escolha do
   * usuário caso o fetch chegue depois do click).
   */
  private tryPreselectSport(): void {
    if (this.form.controls.sportId.value != null) return;
    const sports = this.sports();
    if (sports.length === 1 && sports[0].id != null) {
      this.form.controls.sportId.setValue(sports[0].id);
    }
  }

  submit(): void {
    if (!this.form.valid || this.isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    // Bloqueia submit se há conflito com slot existente. Backend também
    // rejeitaria — mas evitamos a chamada e damos feedback imediato.
    if (this.hasConflict()) return;

    const sport = this.sports().find(
      (s) => s.id === this.form.value.sportId
    );
    if (!sport) return;

    const duration = this.form.value.durationMin ?? this.payload.durationMin;
    const startDate = composeDateTime(this.payload.date, this.payload.slotTime);
    const endDate = composeDateTime(this.payload.date, this.endTimeLabel());
    if (!startDate || !endDate) return;

    const request: UnusualPeriodRequest = {
      startDate,
      endDate,
      courts: [{ id: this.payload.courtId } as Court],
      reason: (this.form.value.reason ?? 'Horário estendido').trim(),
      maxPlayers: this.form.value.maxPlayers ?? 1,
      slotType: (this.form.value.slotType ??
        BookingType.ALUGUEL) as UnusualPeriodRequest['slotType'],
      // Backend aceita intervalInMinutes em minutos (number). Como este modal
      // cria 1 slot único = duração total, intervalo == durationMin do form.
      intervalInMinutes: duration,
      price: parseBrlToNumber(this.form.value.price ?? ''),
      sport: {
        id: sport.id,
        name: sport.name,
        defaultMaxPlayer: sport.defaultMaxPlayer,
      } as Sport,
    };

    this.isSubmitting.set(true);
    this.store.dispatch(addUnusualPeriod({ unsualPeriod: request }));
  }

  dismiss(): void {
    this.modalCtrl.dismiss({ created: false });
  }
}

/** "12:20" → 740 (minutos desde meia-noite). null em formato inválido. */
function parseHHmm(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (isNaN(h) || isNaN(mm)) return null;
  return h * 60 + mm;
}

/** Combina "yyyy-MM-dd" + "HH:mm" em ISO "yyyy-MM-ddTHH:mm:ss". */
function composeDateTime(date?: string, time?: string): string | null {
  if (!date || !time) return null;
  const day = date.includes('T') ? date.split('T')[0] : date;
  const t = time.length === 5 ? `${time}:00` : time;
  return `${day}T${t}`;
}

/** "R$ 70,00" / "70,00" / 70 → 70. Tolerante a undefined. */
function parseBrlToNumber(raw: unknown): number {
  if (raw == null) return 0;
  if (typeof raw === 'number') return raw;
  const str = String(raw)
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = parseFloat(str);
  return Number.isFinite(n) ? n : 0;
}
